import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  Chip,
  Button,
  useTheme,
} from '@mui/material';
import { tokens } from '../../theme';
import CoordinateGrid from './CoordinateGrid';
import GridControls from './GridControls';
import useCoordinateGrid from './useCoordinateGrid';
import { defaultCoordinateConfig, parseFraction, formatElementSummary, isCurveMode } from './coordinateUtils';

/**
 * Admin Component for Configuring Coordinate Grid Questions
 * Allows setting grid parameters and defining correct answers
 */
const CoordinateGridAdmin = ({
  config = defaultCoordinateConfig,
  onConfigChange,
  correctAnswer = { elements: [] },
  onAnswerChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const gridHook = useCoordinateGrid(correctAnswer, config);
  const {
    elements,
    mode,
    setMode,
    pendingPoints,
    canUndo,
    undo,
    clear,
    handleGridClick,
    cancelOperation,
    getValue,
    setValue,
    addEquationLine,
    addTypedPoint,
    addCurveEquation,
  } = gridHook;

  // State for line input mode (twoPoints or equation)
  const [lineInputMode, setLineInputMode] = useState('twoPoints');
  const [equationInput, setEquationInput] = useState('');
  const [equationError, setEquationError] = useState('');

  // State for point input mode (click or type)
  const [pointInputMode, setPointInputMode] = useState('click');
  const [coordinateInput, setCoordinateInput] = useState('');
  const [coordinateError, setCoordinateError] = useState('');

  // State for curve input mode (click or equation for admin)
  const [curveInputMode, setCurveInputMode] = useState('click');
  const [curveEquationInput, setCurveEquationInput] = useState('');
  const [curveEquationError, setCurveEquationError] = useState('');

  // State for logarithmic base selection
  const [logBase, setLogBase] = useState(10);
  const [customLogBase, setCustomLogBase] = useState('');

  // Derived flags: whether each input method group is relevant
  const hasPointMode = (config.allowedModes || []).includes('point');
  const hasLineMode = (config.allowedModes || []).includes('line');
  const hasAnyCurveMode = (config.allowedModes || []).some(m => isCurveMode(m));

  // State for grid input fields (to support fractions)
  const gridConfig = config.grid || defaultCoordinateConfig.grid;
  // Backward compatibility: use xStep/yStep if available, fallback to step
  const xStepValue = gridConfig.xStep || gridConfig.step || 1;
  const yStepValue = gridConfig.yStep || gridConfig.step || 1;
  const [gridInputs, setGridInputs] = useState({
    xMin: String(gridConfig.xMin),
    xMax: String(gridConfig.xMax),
    yMin: String(gridConfig.yMin),
    yMax: String(gridConfig.yMax),
    xStep: String(xStepValue),
    yStep: String(yStepValue),
  });
  const [gridInputErrors, setGridInputErrors] = useState({
    xMin: '',
    xMax: '',
    yMin: '',
    yMax: '',
    xStep: '',
    yStep: '',
  });

  // Sync grid inputs when config changes from parent (but preserve user's fraction notation)
  useEffect(() => {
    const newGridConfig = config.grid || defaultCoordinateConfig.grid;
    // Backward compatibility for step values
    const newXStep = newGridConfig.xStep || newGridConfig.step || 1;
    const newYStep = newGridConfig.yStep || newGridConfig.step || 1;

    setGridInputs((prev) => {
      const updates = {};
      const rangeFields = ['xMin', 'xMax', 'yMin', 'yMax'];

      for (const field of rangeFields) {
        const configValue = newGridConfig[field];
        const currentParsed = parseFraction(prev[field]);

        // Only update display if the numeric value actually changed
        // This preserves "1/2" when config value is 0.5
        if (!currentParsed.valid || Math.abs(currentParsed.value - configValue) > 1e-10) {
          updates[field] = String(configValue);
        }
      }

      // Handle step fields with backward compatibility
      const xStepParsed = parseFraction(prev.xStep);
      if (!xStepParsed.valid || Math.abs(xStepParsed.value - newXStep) > 1e-10) {
        updates.xStep = String(newXStep);
      }
      const yStepParsed = parseFraction(prev.yStep);
      if (!yStepParsed.valid || Math.abs(yStepParsed.value - newYStep) > 1e-10) {
        updates.yStep = String(newYStep);
      }

      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
    // Clear errors for fields that were updated from parent
    setGridInputErrors({ xMin: '', xMax: '', yMin: '', yMax: '', xStep: '', yStep: '' });
  }, [config.grid]);

  // Sync external correctAnswer with internal state
  useEffect(() => {
    if (JSON.stringify(correctAnswer?.elements) !== JSON.stringify(elements)) {
      setValue(correctAnswer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctAnswer]);

  // Notify parent when elements change
  useEffect(() => {
    const currentValue = getValue();
    if (JSON.stringify(currentValue.elements) !== JSON.stringify(correctAnswer?.elements)) {
      onAnswerChange?.(currentValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);

  // Reset mode if current mode is no longer in allowedModes
  useEffect(() => {
    const allowedModes = config.allowedModes || ['point'];
    if (!allowedModes.includes(mode)) {
      // Switch to the first allowed mode
      setMode(allowedModes[0] || 'point');
    }
  }, [config.allowedModes, mode, setMode]);

  // Handle config field changes
  const handleGridConfigChange = useCallback(
    (field, value) => {
      const newConfig = {
        ...config,
        grid: {
          ...config.grid,
          [field]: value,
        },
      };
      onConfigChange?.(newConfig);
    },
    [config, onConfigChange]
  );

  // Handle grid input changes with fraction support
  const handleGridInputChange = useCallback(
    (field, inputValue) => {
      setGridInputs((prev) => ({ ...prev, [field]: inputValue }));

      const result = parseFraction(inputValue);
      if (!result.valid) {
        setGridInputErrors((prev) => ({ ...prev, [field]: result.error }));
        return;
      }

      if ((field === 'xStep' || field === 'yStep') && result.value <= 0) {
        setGridInputErrors((prev) => ({ ...prev, [field]: 'Step must be positive' }));
        return;
      }

      setGridInputErrors((prev) => ({ ...prev, [field]: '' }));
      handleGridConfigChange(field, result.value);
    },
    [handleGridConfigChange]
  );

  // Handle toggling array values (for input methods)
  const handleToggleArrayValue = useCallback(
    (field, value) => {
      const currentArray = config[field] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter((v) => v !== value)
        : [...currentArray, value];

      // At least one must remain selected
      if (newArray.length === 0) return;

      onConfigChange?.({ ...config, [field]: newArray });
    },
    [config, onConfigChange]
  );

  // Handle adding equation line
  const handleAddEquationLine = useCallback(() => {
    const gridBounds = {
      xMin: gridConfig.xMin,
      xMax: gridConfig.xMax,
      yMin: gridConfig.yMin,
      yMax: gridConfig.yMax,
    };
    const result = addEquationLine(equationInput, gridBounds);
    if (result?.error) {
      setEquationError(result.error);
    } else {
      setEquationInput('');
      setEquationError('');
    }
  }, [addEquationLine, equationInput, gridConfig]);

  // Handle adding typed point
  const handleAddTypedPoint = useCallback(() => {
    const gridBounds = {
      xMin: gridConfig.xMin,
      xMax: gridConfig.xMax,
      yMin: gridConfig.yMin,
      yMax: gridConfig.yMax,
    };
    const result = addTypedPoint(coordinateInput, gridBounds);
    if (result?.error) {
      setCoordinateError(result.error);
    } else {
      setCoordinateInput('');
      setCoordinateError('');
    }
  }, [addTypedPoint, coordinateInput, gridConfig]);

  // Handle adding curve equation
  const handleAddCurveEquation = useCallback(() => {
    const gridBounds = {
      xMin: gridConfig.xMin,
      xMax: gridConfig.xMax,
      yMin: gridConfig.yMin,
      yMax: gridConfig.yMax,
    };
    const result = addCurveEquation(curveEquationInput, gridBounds);
    if (result?.error) {
      setCurveEquationError(result.error);
    } else {
      setCurveEquationInput('');
      setCurveEquationError('');
    }
  }, [addCurveEquation, curveEquationInput, gridConfig]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Settings Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: colors.grey[100] }}>
          Grid Configuration
        </Typography>

        {/* Axis Range */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: colors.grey[400], mb: 1, display: 'block' }}>
              X-axis Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Min"
                type="text"
                size="small"
                value={gridInputs.xMin}
                onChange={(e) => handleGridInputChange('xMin', e.target.value)}
                error={!!gridInputErrors.xMin}
                helperText={gridInputErrors.xMin || 'e.g., -10, 1/2'}
                sx={{ width: 100 }}
              />
              <Typography sx={{ color: colors.grey[400], mt: 1 }}>to</Typography>
              <TextField
                label="Max"
                type="text"
                size="small"
                value={gridInputs.xMax}
                onChange={(e) => handleGridInputChange('xMax', e.target.value)}
                error={!!gridInputErrors.xMax}
                helperText={gridInputErrors.xMax || 'e.g., 10, 3/2'}
                sx={{ width: 100 }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: colors.grey[400], mb: 1, display: 'block' }}>
              Y-axis Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Min"
                type="text"
                size="small"
                value={gridInputs.yMin}
                onChange={(e) => handleGridInputChange('yMin', e.target.value)}
                error={!!gridInputErrors.yMin}
                helperText={gridInputErrors.yMin || 'e.g., -10, 1/2'}
                sx={{ width: 100 }}
              />
              <Typography sx={{ color: colors.grey[400], mt: 1 }}>to</Typography>
              <TextField
                label="Max"
                type="text"
                size="small"
                value={gridInputs.yMax}
                onChange={(e) => handleGridInputChange('yMax', e.target.value)}
                error={!!gridInputErrors.yMax}
                helperText={gridInputErrors.yMax || 'e.g., 10, 3/2'}
                sx={{ width: 100 }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: colors.grey[400], mb: 1, display: 'block' }}>
              Grid Step
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="X"
                type="text"
                size="small"
                value={gridInputs.xStep}
                onChange={(e) => handleGridInputChange('xStep', e.target.value)}
                error={!!gridInputErrors.xStep}
                helperText={gridInputErrors.xStep || 'e.g., 1, 1/2'}
                sx={{ width: 100 }}
              />
              <TextField
                label="Y"
                type="text"
                size="small"
                value={gridInputs.yStep}
                onChange={(e) => handleGridInputChange('yStep', e.target.value)}
                error={!!gridInputErrors.yStep}
                helperText={gridInputErrors.yStep || 'e.g., 1, 1/2'}
                sx={{ width: 100 }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Display Options and Allowed Modes - Side by Side */}
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {/* Display Options */}
          <Box>
            <Typography variant="caption" sx={{ color: colors.grey[400], mb: 1, display: 'block' }}>
              Display Options
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={gridConfig.showGridLines !== false}
                    onChange={(e) => handleGridConfigChange('showGridLines', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Show Grid Lines</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={gridConfig.showAxisLabels !== false}
                    onChange={(e) => handleGridConfigChange('showAxisLabels', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Show Axis Labels</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={gridConfig.snapToGrid !== false}
                    onChange={(e) => handleGridConfigChange('snapToGrid', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Snap to Grid</Typography>}
              />
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Student Input Methods */}
          <Box>
            <Typography variant="caption" sx={{ color: colors.grey[400], mb: 1, display: 'block' }}>
              Student Input Methods
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: hasPointMode ? colors.grey[300] : colors.grey[600], fontWeight: 500 }}>
                Point Input:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedPointInputMethods || ['click', 'type']).includes('click')}
                      onChange={() => handleToggleArrayValue('allowedPointInputMethods', 'click')}
                      size="small"
                      disabled={
                        !hasPointMode ||
                        ((config.allowedPointInputMethods || ['click', 'type']).length === 1 &&
                        (config.allowedPointInputMethods || ['click', 'type']).includes('click'))
                      }
                    />
                  }
                  label={<Typography variant="body2">Click on Grid</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedPointInputMethods || ['click', 'type']).includes('type')}
                      onChange={() => handleToggleArrayValue('allowedPointInputMethods', 'type')}
                      size="small"
                      disabled={
                        !hasPointMode ||
                        ((config.allowedPointInputMethods || ['click', 'type']).length === 1 &&
                        (config.allowedPointInputMethods || ['click', 'type']).includes('type'))
                      }
                    />
                  }
                  label={<Typography variant="body2">Type Coordinates</Typography>}
                />
              </Box>
              <Typography variant="caption" sx={{ color: hasLineMode ? colors.grey[300] : colors.grey[600], fontWeight: 500, mt: 1 }}>
                Line Input:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedLineInputMethods || ['twoPoints', 'equation']).includes('twoPoints')}
                      onChange={() => handleToggleArrayValue('allowedLineInputMethods', 'twoPoints')}
                      size="small"
                      disabled={
                        !hasLineMode ||
                        ((config.allowedLineInputMethods || ['twoPoints', 'equation']).length === 1 &&
                        (config.allowedLineInputMethods || ['twoPoints', 'equation']).includes('twoPoints'))
                      }
                    />
                  }
                  label={<Typography variant="body2">Two Points</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedLineInputMethods || ['twoPoints', 'equation']).includes('equation')}
                      onChange={() => handleToggleArrayValue('allowedLineInputMethods', 'equation')}
                      size="small"
                      disabled={
                        !hasLineMode ||
                        ((config.allowedLineInputMethods || ['twoPoints', 'equation']).length === 1 &&
                        (config.allowedLineInputMethods || ['twoPoints', 'equation']).includes('equation'))
                      }
                    />
                  }
                  label={<Typography variant="body2">Equation</Typography>}
                />
              </Box>
              <Typography variant="caption" sx={{ color: hasAnyCurveMode ? colors.grey[300] : colors.grey[600], fontWeight: 500, mt: 1 }}>
                Curve Input:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedCurveInputMethods || ['click', 'type']).includes('click')}
                      onChange={() => handleToggleArrayValue('allowedCurveInputMethods', 'click')}
                      size="small"
                      disabled={
                        !hasAnyCurveMode ||
                        ((config.allowedCurveInputMethods || ['click', 'type']).length === 1 &&
                        (config.allowedCurveInputMethods || ['click', 'type']).includes('click'))
                      }
                    />
                  }
                  label={<Typography variant="body2">Click Points</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedCurveInputMethods || ['click', 'type']).includes('type')}
                      onChange={() => handleToggleArrayValue('allowedCurveInputMethods', 'type')}
                      size="small"
                      disabled={
                        !hasAnyCurveMode ||
                        ((config.allowedCurveInputMethods || ['click', 'type']).length === 1 &&
                        (config.allowedCurveInputMethods || ['click', 'type']).includes('type'))
                      }
                    />
                  }
                  label={<Typography variant="body2">Type Coordinates</Typography>}
                />
              </Box>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Allowed Student Modes */}
          <Box>
            <Typography variant="caption" sx={{ color: colors.grey[400], mb: 1, display: 'block' }}>
              Allowed Drawing Modes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: colors.grey[300], fontWeight: 500 }}>
                Basic:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, ml: 1, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('point')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'point')}
                      size="small"
                      disabled={(config.allowedModes || ['point']).length === 1 && (config.allowedModes || ['point']).includes('point')}
                    />
                  }
                  label={<Typography variant="body2">Point</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('line')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'line')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Line</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('polygon')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'polygon')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Polygon</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('circle')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'circle')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Circle</Typography>}
                />
              </Box>
              <Typography variant="caption" sx={{ color: colors.grey[300], fontWeight: 500, mt: 1 }}>
                Curves:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, ml: 1, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('quadratic')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'quadratic')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Parabola</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('absolute')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'absolute')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Abs Value</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('sqrt')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'sqrt')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Sqrt</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('nthRoot')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'nthRoot')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Nth Root</Typography>}
                />
                {(config.allowedModes || []).includes('nthRoot') && (
                  <TextField
                    type="number"
                    size="small"
                    label="Root index n"
                    value={config.nthRootIndex ?? 3}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value || '3', 10);
                      const safe = Number.isInteger(raw) && raw >= 2 ? raw : 2;
                      onConfigChange?.({ ...config, nthRootIndex: safe });
                    }}
                    inputProps={{ min: 2, step: 1 }}
                    sx={{ width: 110, ml: 1 }}
                  />
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('exponential')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'exponential')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Exp</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('logarithmic')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'logarithmic')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Log</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('sine')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'sine')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Sine</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('cosine')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'cosine')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Cosine</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('tangent')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'tangent')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Tangent</Typography>}
                />
              </Box>
              {/* Polynomial Curves */}
              <Typography variant="caption" sx={{ fontWeight: 'bold', mt: 1, display: 'block' }}>
                Polynomial Curves:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('polynomial2')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'polynomial2')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Degree 2 (3 pts)</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('polynomial3')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'polynomial3')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Degree 3 (4 pts)</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('polynomial4')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'polynomial4')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Degree 4 (5 pts)</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(config.allowedModes || ['point']).includes('polynomial5')}
                      onChange={() => handleToggleArrayValue('allowedModes', 'polynomial5')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Degree 5 (6 pts)</Typography>}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Grid Preview Section */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[100] }}>
          Set Correct Answer
        </Typography>
        <Typography variant="caption" sx={{ color: colors.grey[400], mb: 1, display: 'block' }}>
          Click on the grid to add the correct answer elements
        </Typography>

        {/* Controls */}
        <GridControls
          mode={mode}
          setMode={setMode}
          allowedModes={config.allowedModes || ['point']} // Only show modes that are enabled in configuration
          onUndo={undo}
          onClear={clear}
          onCancel={cancelOperation}
          canUndo={canUndo}
          hasElements={elements.length > 0}
          hasPending={pendingPoints.length > 0}
          lineInputMode={lineInputMode}
          setLineInputMode={setLineInputMode}
          pointInputMode={pointInputMode}
          setPointInputMode={setPointInputMode}
          curveInputMode={curveInputMode}
          setCurveInputMode={setCurveInputMode}
          allowedCurveInputMethods={['click', 'equation']}
          logBase={logBase}
          setLogBase={setLogBase}
          customLogBase={customLogBase}
          setCustomLogBase={setCustomLogBase}
        />

        {/* Equation input for line mode */}
        {mode === 'line' && lineInputMode === 'equation' && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Enter equation"
              value={equationInput}
              onChange={(e) => {
                setEquationInput(e.target.value);
                setEquationError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddEquationLine();
                }
              }}
              placeholder="y = 2x + 1"
              error={!!equationError}
              helperText={equationError || 'Formats: y=mx+b, ax+by=c, x=c, y=c'}
              size="small"
              sx={{ width: 200 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddEquationLine}
              size="small"
              sx={{ mt: 0.5 }}
            >
              Add Line
            </Button>
          </Box>
        )}

        {/* Coordinate input for point mode */}
        {mode === 'point' && pointInputMode === 'type' && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Enter coordinates"
              value={coordinateInput}
              onChange={(e) => {
                setCoordinateInput(e.target.value);
                setCoordinateError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTypedPoint();
                }
              }}
              placeholder="(1/2, 3/4) or (0.5, -1)"
              error={!!coordinateError}
              helperText={coordinateError || 'Supports fractions: (1/2, 3/4)'}
              size="small"
              sx={{ width: 220 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddTypedPoint}
              size="small"
              sx={{ mt: 0.5 }}
            >
              Add Point
            </Button>
          </Box>
        )}

        {/* Curve equation input for curve modes */}
        {isCurveMode(mode) && curveInputMode === 'equation' && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Enter curve equation"
              value={curveEquationInput}
              onChange={(e) => {
                setCurveEquationInput(e.target.value);
                setCurveEquationError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCurveEquation();
                }
              }}
              placeholder="y = (1/2)^x"
              error={!!curveEquationError}
              helperText={curveEquationError || 'e.g., y=x^2+2x+1, y=|x-2|+1, y=sqrt(x-2)+1, y=sqrt(-x+4) (reflected), y=(1/2)^x, y=sin(2x), y=log_2(x+3)+1, y=tan(pix/2), y=\\sqrt[3]{x-1}+2 — for trig, put x inside the parentheses: tan(pix/2), not tan(pi/2)x'}
              size="small"
              sx={{ width: 280 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddCurveEquation}
              size="small"
              sx={{ mt: 0.5 }}
            >
              Add Curve
            </Button>
          </Box>
        )}

        {/* Grid */}
        <CoordinateGrid
          config={gridConfig}
          elements={elements}
          pendingPoints={pendingPoints}
          mode={mode}
          readOnly={false}
          onGridClick={handleGridClick}
          width={380}
          height={380}
        />

        {/* Answer Summary */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mb: 1 }}>
            Correct Answer ({elements.length} element{elements.length !== 1 ? 's' : ''}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {elements.length === 0 ? (
              <Typography variant="caption" sx={{ color: colors.grey[500], fontStyle: 'italic' }}>
                No elements added yet
              </Typography>
            ) : (
              elements.map((el, i) => (
                <Chip
                  key={el.id || i}
                  label={formatElementSummary(el)}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: colors.blueAccent[500],
                    color: colors.grey[200],
                    fontSize: '0.7rem',
                  }}
                />
              ))
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CoordinateGridAdmin;
